import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import { viewerHasRole } from '../lib/raceViewerRoles';

configure({ enforceActions: 'always' });

class RaceStore extends LocalModel {
    constructor() {
        super('race', {
            namePlural: 'races',
            namePluralReal: 'races',
            sort: 'startDate',
            limit: 500,
            api: {
                search: {
                    url: '/api/races/',
                    params: {
                        limit: 15,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/races/',
                    params: {},
                },
                save: {
                    url: '/api/races/',
                    params: {},
                },
                delete: {
                    url: '/api/races/',
                    params: {},
                },
            },
        });
    }

    @observable reloadTimer = null;

    @observable trackingReloadTimer = null;

    @observable newRace = {};

    @observable race = {};

    @observable races = [];

    @observable myRaces = [];

    @observable trackerCalendarRaces = [];

    @observable trackerPoolSize = 0;

    @observable trackerCalendarError = '';

    @observable raceClassList = [];

    @observable raceAdmins = [];

    @observable customers = [];

    @observable isRaceAdmin = false;

    @observable isMediaWorker = false;

    @observable isTimeTracker = false;

    @observable isTdLeader = false;

    @observable isRaceLeader = false;

    @observable isTvRadioProducer = false;

    @observable hasStarted = false;

    @observable raceWorkerUsers = [];

    @observable raceDuplicate = {};

    @action
    setReloadTimer = (timer) => {
        if (this.reloadTimer) {
            clearTimeout(this.reloadTimer);
        }
        this.reloadTimer = timer;
    }

    @action
    deleteReloadTimer = () => {
        clearTimeout(this.reloadTimer);
    }

    @action
    setTrackingReloadTimer = (timer) => {
        if (this.trackingReloadTimer) {
            clearTimeout(this.trackingReloadTimer);
        }
        this.trackingReloadTimer = timer;
    }

    @action
    deleteTrackingReloadTimer = () => {
        clearTimeout(this.trackingReloadTimer);
    }

    @action
    checkRace = () => {
        if (new Date(this.race?.startDate) < new Date()) {
            this.hasStarted = true;
        } else {
            this.hasStarted = false;
        }
    }

    @action
    checkRaceAdmin = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isRaceAdmin')) {
                this.isRaceAdmin = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    @action
    checkMediaWorker = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isMediaWorker')) {
                this.isMediaWorker = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    @action
    checkTimetracker = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isTimeTracker')) {
                this.isTimeTracker = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    @action
    checkTdLeader = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isTdLeader')) {
                this.isTdLeader = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    @action
    checkRaceLeader = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isRaceLeader')) {
                this.isRaceLeader = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    @action
    checkTvRadioProducer = (user) => {
        try {
            if (viewerHasRole(this.race, user, 'isTvRadioProducer')) {
                this.isTvRadioProducer = true;
                return true;
            }
            return false;
        } catch(e) {
            this.reportError(e);
            return false;
        }
    }

    checkAllPositions = (user) => {
        this.checkRaceAdmin(user);
        this.checkMediaWorker(user);
        this.checkTimetracker(user);
        this.checkTdLeader(user);
        this.checkRaceLeader(user);
        this.checkTvRadioProducer(user);
    }

    // Same shape as loadMyRaces below: no @action on the arrow property; every
    // observable write goes through updateKeyValue, which is the action.
    loadTrackerCalendar = async ({ from, to } = {}) => {
        const response = await util.fetchApi('/api/races/admin/tracker-calendar', { method: 'GET' }, { from, to });
        if (response.status === 200) {
            this.updateKeyValue('trackerCalendarRaces', response.data || []);
            this.updateKeyValue('trackerPoolSize', response.included?.trackerPoolSize || 0);
            this.updateKeyValue('trackerCalendarError', '');
        } else {
            this.updateKeyValue('trackerCalendarRaces', []);
            this.updateKeyValue('trackerCalendarError', response.message || `HTTP ${response.status}`);
        }
        return response;
    }

    loadMyRaces = async (props = this.props) => {
        const response = await util.fetchApi(`/api/races/myraces`, { publish: true, method: 'GET' }, {
            ...props,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('myRaces', response.data);
            case 401:
                // PubSub.publish(topics.LOG_OUT);
                // route('/');
                break;
        }
    }

    async loadPublic(opt = { query: {} }, updateObject) {
        const response = await util.fetchApi(`/api/races/public`, {  method: 'GET' }, { ...opt.query, limit: opt.limit || 1000 });
        switch (response.status) {
            case 200:
                this.updateKeyValue('races', response.data);
                if (updateObject) {
                    this.updateKeyValue('race', response.data[0]);
                }
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async searchUsersForRace(search) {
        if (!search || search.length < 2) {
            this.updateKeyValue('raceWorkerUsers', []);
            return [];
        }
        const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search });
        if (response.status === 200) {
            this.updateKeyValue('raceWorkerUsers', response.data || []);
            return response.data || [];
        }
        return [];
    }

    async validateRace(raceId) {
        if (!raceId) {
            return null;
        }
        const response = await util.fetchApi(`/api/races/${raceId}/validate`, { method: 'GET' });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async validateRaceResults(raceId) {
        if (!raceId) {
            return null;
        }
        const response = await util.fetchApi(`/api/races/${raceId}/validate-results`, { method: 'GET' });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async getGeofenceStates(raceId) {
        if (!raceId) {
            return null;
        }
        const response = await util.fetchApi(`/api/races/${raceId}/geofence-states`, { method: 'GET' });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async getGeofenceDebug(raceId, contestantId) {
        if (!raceId || !contestantId) {
            return null;
        }
        const response = await util.fetchApi(`/api/races/${raceId}/geofence-debug/${contestantId}`, { method: 'GET' });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async duplicateRace(raceId, { startDate, title }) {
        if (!raceId) {
            return null;
        }
        const response = await util.fetchApi(`/api/races/duplicate/${raceId}`, { method: 'POST' }, {
            startDate,
            title,
        });
        switch (response.status) {
            case 201:
                this.updateKeyValue('raceDuplicate', response.data?.race || {});
                return response.data;
            default:
                return null;
        }
    }
}

const store = new RaceStore();
export default store;
